import { Link } from "react-router-dom";
import {
  ArrowRight,
  Heart,
  Lock,
  Coins,
  HandHeart,
  Quote as QuoteIcon,
  Pill,
  ShieldCheck,
  Stethoscope,
  Microscope,
  ClipboardList,
} from "lucide-react";
import { METHODOLOGIES } from "../constants/methodologies";
import { usePageMeta } from "../hooks/usePageMeta";

export default function AboutPage() {
  usePageMeta({
    path: "/about",
    title:
      "About MediTalk , Healthcare research platform by Medicys",
    description:
      "MediTalk closes the gap between patients, caregivers and the healthcare industry , one honest conversation at a time. Built and run in the UK by Medicys, an independent healthcare market-research consultancy serving global studies.",
  });
  return (
    <div>
      {/* ============================================================
          HERO , DARK. The opener acknowledges them before pitching us.
          ============================================================ */}
      <section className="bg-[var(--dark-bg)] grain-dark relative overflow-hidden">
        <div className="absolute -top-32 -right-24 w-[420px] h-[420px] rounded-full bg-[var(--lime)]/10 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 -left-32 w-[340px] h-[340px] rounded-full bg-[var(--lime)]/5 blur-3xl pointer-events-none" />

        <div className="container-main py-20 md:py-28 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-8">
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-[var(--lime)] mb-6">
                <span className="w-8 h-px bg-[var(--lime)]" />
                About MediTalk
              </span>
              <h1 className="font-['Outfit'] font-bold text-white leading-[1.05] tracking-tight text-4xl md:text-5xl lg:text-[58px] mb-6">
                You've lived it.
                <span className="text-[var(--lime)]"> We're here to listen.</span>
              </h1>
              <p className="text-lg md:text-xl text-[var(--gray-light)] leading-relaxed max-w-xl">
                If you live with a condition, or care for someone who does,
                you know things no doctor, researcher or algorithm ever could.
                MediTalk exists so that knowledge actually shapes the medicines,
                treatments and tools designed for people like you.
              </p>
            </div>
            <div className="lg:col-span-4">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10 max-w-[280px] mx-auto lg:max-w-none">
                <img
                  src="/images/photos/hero-register.jpg"
                  alt="Hands joined together in solidarity"
                  className="w-full aspect-square object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-[var(--charcoal)]/40 via-transparent to-transparent pointer-events-none" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          WHY THIS EXISTS , LIGHT. The problem we're trying to fix.
          ============================================================ */}
      <section className="py-20 md:py-24 bg-white">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7">
              <span className="badge mb-4">Why we exist</span>
              <h2 className="text-3xl md:text-4xl font-['Outfit'] font-bold text-[var(--charcoal)] leading-tight mb-6">
                Healthcare has been built <em>for</em> patients,
                rarely <em>with</em> them.
              </h2>
              <div className="space-y-5 text-[var(--text-muted)] leading-relaxed text-lg">
                <p>
                  Treatments get designed. Patient leaflets get written. Dosing
                  schedules get set. Trial criteria get drafted, most of it
                  without ever asking the people who'll actually live with
                  them what works, what doesn't, and what gets in the way.
                </p>
                <p>
                  The result? Medicines that are technically effective but
                  practically miserable to take. Support materials nobody
                  reads. Caregivers exhausted by gaps no one thought to fill.
                </p>
                <p className="text-[var(--charcoal)] font-semibold text-xl md:text-2xl leading-snug pt-2">
                  <span
                    className="px-1.5 box-decoration-clone"
                    style={{
                      backgroundImage:
                        "linear-gradient(180deg, transparent 0 55%, var(--lime) 55% 92%, transparent 92% 100%)",
                    }}
                  >
                    We exist to close that gap, one honest conversation at a time.
                  </span>
                </p>
              </div>
            </div>

            <aside className="lg:col-span-5 lg:sticky lg:top-28">
              <div className="rounded-3xl bg-[var(--bone-soft)] border border-[var(--light-border)] p-8 md:p-10">
                <QuoteIcon
                  size={32}
                  className="text-[var(--lime)] mb-4"
                  strokeWidth={1.5}
                />
                <p className="text-2xl md:text-3xl font-['Outfit'] font-semibold text-[var(--charcoal)] leading-snug mb-6">
                  "I've explained my condition a hundred times: to doctors, to
                  family, to insurers. This was the first time anyone really
                  wanted to know."
                </p>
                <p className="text-sm text-[var(--text-muted)]">
                  <span className="font-semibold text-[var(--charcoal)]">Oliver</span> · MediTalk participant
                </p>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* ============================================================
          WHAT WE PROMISE , DARK. Plain commitments, no corporate fluff.
          ============================================================ */}
      <section className="bg-[var(--dark-bg)] grain-dark py-20 md:py-24 relative">
        <div className="container-main relative z-10">
          <div className="max-w-2xl mb-14">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-[var(--lime)] mb-4">
              <span className="w-8 h-px bg-[var(--lime)]" />
              Our promises to you
            </span>
            <h2 className="text-3xl md:text-4xl font-['Outfit'] font-bold text-white leading-tight">
              Four things you can hold us to.
            </h2>
          </div>

          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
            <Promise
              icon={Heart}
              title="You're never just a data point"
              body="A real person sets up the study, listens to your story, asks follow-up questions. You're a contributor, not a survey response."
            />
            <Promise
              icon={Coins}
              title="Your time has value, and we pay for it"
              body="Every study is paid. We say thank-you with cash, vouchers or charitable donations (your choice) at fair industry rates."
            />
            <Promise
              icon={Lock}
              title="Your privacy isn't a checkbox, it's a foundation"
              body="Your details are encrypted, anonymized in research outputs, and never sold. We only contact you about studies that match, and you can leave at any moment."
            />
            <Promise
              icon={HandHeart}
              title="You decide what you share"
              body="If a question feels too personal, skip it. If a study isn't right, say no. Your involvement is voluntary, every step of the way."
            />
          </ul>
        </div>
      </section>

      {/* ============================================================
          WHAT TAKING PART FEELS LIKE , LIGHT.
          All 8 methodologies, each with a realistic time commitment.
          ============================================================ */}
      <section className="py-20 md:py-24 bg-white">
        <div className="container-main">
          <div className="max-w-2xl mb-12">
            <span className="badge mb-4">What it actually looks like</span>
            <h2 className="text-3xl md:text-4xl font-['Outfit'] font-bold text-[var(--charcoal)] leading-tight">
              No clinics. No trials. Just a few of the typical ways
              people take part, on the format that fits you.
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {METHODOLOGIES.map((m) => (
              <FormatCard
                key={m.key}
                icon={m.icon}
                time={m.time}
                title={m.title}
                body={m.body}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          WHERE YOUR VOICE GOES , DARK. Tangible impact.
          ============================================================ */}
      <section className="bg-[var(--dark-bg)] grain-dark py-20 md:py-24 relative">
        <div className="container-main relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5 order-2 lg:order-1">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl ring-1 ring-white/10">
                <img
                  src="/images/photos/scene-research.jpg"
                  alt="A research conversation in progress"
                  className="w-full aspect-square object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-[var(--charcoal)]/40 via-transparent to-transparent pointer-events-none" />
              </div>
            </div>

            <div className="lg:col-span-7 order-1 lg:order-2">
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-[var(--lime)] mb-4">
                <span className="w-8 h-px bg-[var(--lime)]" />
                Where your voice goes
              </span>
              <h2 className="text-3xl md:text-4xl font-['Outfit'] font-bold text-white leading-tight mb-6">
                Your words shape real things.
              </h2>
              <p className="text-[var(--gray-light)] leading-relaxed mb-8 max-w-lg">
                Powered by{" "}
                <a
                  href="https://medicysltd.co.uk/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white font-semibold underline decoration-[var(--lime)] decoration-2 underline-offset-4 hover:text-[var(--lime)] transition-colors"
                  data-testid="about-medicys-link"
                >
                  Medicys<span className="text-[0.55em] align-top tracking-normal ml-px" aria-label="registered trademark">®</span>
                </a>
                , a leading healthcare research fieldwork agency, MediTalk works with
                pharmaceutical companies, biotech firms, research institutions
                and healthcare regulators worldwide. Anonymized insights from
                your conversation feed directly into the teams who design and
                deliver care.
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 max-w-lg">
                {[
                  { icon: Pill, text: "Medicines & dosing schedules" },
                  { icon: ClipboardList, text: "Patient leaflets & packaging" },
                  { icon: Microscope, text: "Clinical trial design" },
                  { icon: Stethoscope, text: "Treatment & support pathways" },
                ].map(({ icon: Icon, text }) => (
                  <li
                    key={text}
                    className="flex items-center gap-2.5 text-sm text-[var(--gray-light)]"
                  >
                    <Icon size={16} className="text-[var(--lime)] flex-shrink-0" />
                    {text}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          TWO COMMUNITIES , LIGHT.
          Two distinct audiences, one mission. Replaces the old thin
          "Our community" section.
          ============================================================ */}
      <section className="py-20 md:py-24 bg-white">
        <div className="container-main">
          <div className="max-w-2xl mb-14">
            <span className="badge mb-4">Our community</span>
            <h2 className="text-3xl md:text-4xl font-['Outfit'] font-bold text-[var(--charcoal)] leading-tight mb-5">
              Two communities.{" "}
              <span
                className="px-1.5 leading-none"
                style={{
                  backgroundImage:
                    "linear-gradient(180deg, transparent 0 55%, var(--lime) 55% 92%, transparent 92% 100%)",
                }}
              >
                One mission.
              </span>
            </h2>
            <p className="text-[var(--text-muted)] text-lg leading-relaxed">
              MediTalk exists for two groups of people whose voices are rarely
              at the table when healthcare decisions get made. We invite both
              in, and we make sure the industry listens.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 mb-16">
            <CommunityCard
              eyebrow="Community 01"
              title="People living with a medical condition"
              body="You know things no doctor, researcher or algorithm ever could. The diagnosis, the side effects, the late-night Googling, the appointments that ran late. Your story helps shape medicines, trials and support that actually work for the people taking them."
              ctaText="Join as patient"
              ctaTo="/patient/sign-up"
              ctaTestId="about-community-patient-cta"
            />
            <CommunityCard
              eyebrow="Community 02"
              title="Caregivers"
              body="Partners, parents, children, friends. The people who hold it together when someone they love is unwell. Your perspective surfaces the gaps no one else sees, the moments where the system could do better."
              ctaText="Join as caregiver"
              ctaTo="/caregiver/sign-up"
              ctaTestId="about-community-caregiver-cta"
            />
          </div>

          <figure className="max-w-3xl mx-auto text-center pt-4">
            <p className="text-[var(--text-muted)] max-w-2xl mx-auto mb-14">
              Newly diagnosed or twenty years in. Common conditions or rare ones.
              Every kind of family, every kind of life. There's space here for
              your story.
            </p>
            <QuoteIcon
              size={36}
              strokeWidth={1.25}
              className="mx-auto text-[var(--lime)] mb-6"
              aria-hidden="true"
            />
            <blockquote className="font-['Outfit'] font-semibold text-[var(--charcoal)] leading-[1.2] text-3xl md:text-[40px] tracking-tight">
              "Living with this for ten years, I thought no one was listening.{" "}
              <span
                className="px-1.5 box-decoration-clone"
                style={{
                  backgroundImage:
                    "linear-gradient(180deg, transparent 0 55%, var(--lime) 55% 92%, transparent 92% 100%)",
                }}
              >
                Turns out, someone was.
              </span>
              "
            </blockquote>
            <figcaption className="mt-8 text-sm text-[var(--text-muted)]">
              <span className="font-semibold text-[var(--charcoal)]">Mira</span>
              <span> · MediTalk participant, India</span>
            </figcaption>
          </figure>
        </div>
      </section>

      {/* ============================================================
          INVITATION , DARK. Direct, human ask.
          ============================================================ */}
      <section className="bg-[var(--dark-bg)] grain-dark py-20 md:py-24 relative overflow-hidden">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[640px] h-[640px] rounded-full bg-[var(--lime)]/8 blur-3xl pointer-events-none" />
        <div className="container-main text-center relative z-10">
          <ShieldCheck size={32} className="text-[var(--lime)] mx-auto mb-6" />
          <h2 className="text-3xl md:text-5xl font-['Outfit'] font-bold text-white leading-tight mb-5 max-w-2xl mx-auto">
            If you've ever thought
            <span className="text-[var(--lime)]"> "I wish someone would
            actually listen"</span>,
            <br className="hidden md:block" /> we are.
          </h2>
          <p className="text-[var(--gray-light)] max-w-xl mx-auto mb-10 text-lg">
            Registration takes about 3 minutes. We'll only get in touch when
            something matches you, never before.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/patient/sign-up"
              className="btn-primary inline-flex items-center justify-center gap-2"
              data-testid="about-cta-patient"
            >
              I'm a patient
              <ArrowRight size={18} />
            </Link>
            <Link
              to="/caregiver/sign-up"
              className="btn-secondary inline-flex items-center justify-center gap-2"
              data-testid="about-cta-caregiver"
            >
              I'm a caregiver
              <ArrowRight size={18} />
            </Link>
          </div>
          <p className="mt-10 text-sm text-[var(--gray-light)]">
            Questions first?{" "}
            <a
              href="mailto:hello@meditalk.world"
              className="text-[var(--lime)] hover:underline underline-offset-4"
              data-testid="about-contact-email"
            >
              hello@meditalk.world
            </a>
          </p>
        </div>
      </section>
    </div>
  );
}

// ============================================================
// Sub-components
// ============================================================
function Promise({ icon: Icon, title, body }) {
  return (
    <li className="flex gap-4">
      <span className="w-11 h-11 rounded-2xl bg-[var(--lime)]/15 text-[var(--lime)] flex items-center justify-center flex-shrink-0">
        <Icon size={22} strokeWidth={1.75} />
      </span>
      <div className="min-w-0">
        <h3 className="text-white font-['Outfit'] font-semibold text-lg mb-1.5">
          {title}
        </h3>
        <p className="text-[var(--gray-light)] leading-relaxed">{body}</p>
      </div>
    </li>
  );
}

function FormatCard({ icon: Icon, time, title, body }) {
  return (
    <div className="group rounded-2xl border border-[var(--light-border)] bg-white p-5 md:p-6 hover:border-[var(--lime)] hover:shadow-[0_20px_60px_-30px_rgba(196,247,75,0.5)] transition-all">
      <span className="w-11 h-11 rounded-xl bg-[var(--lime)]/15 text-[var(--charcoal)] flex items-center justify-center mb-4">
        <Icon size={22} strokeWidth={1.75} />
      </span>
      <p className="text-xs uppercase tracking-wider font-semibold text-[var(--text-muted)] mb-1.5">
        {time}
      </p>
      <h3 className="font-['Outfit'] font-bold text-[var(--charcoal)] text-lg mb-2 leading-snug">
        {title}
      </h3>
      <p className="text-[var(--text-muted)] leading-relaxed text-sm">
        {body}
      </p>
    </div>
  );
}

function CommunityCard({ eyebrow, title, body, ctaText, ctaTo, ctaTestId }) {
  return (
    <div className="group relative rounded-3xl bg-[var(--bone-soft)] border border-[var(--light-border)] p-8 md:p-10 hover:border-[var(--lime)] hover:shadow-[0_24px_60px_-30px_rgba(196,247,75,0.5)] transition-all flex flex-col">
      <span className="absolute top-6 right-6 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-semibold text-[var(--teal)]">
        <span className="w-5 h-px bg-[var(--teal)]" />
        {eyebrow}
      </span>
      <h3 className="text-2xl md:text-[28px] font-['Outfit'] font-bold text-[var(--charcoal)] leading-tight mb-4 max-w-[22ch]">
        {title}
      </h3>
      <p className="text-[var(--text-muted)] leading-relaxed mb-8 flex-1">
        {body}
      </p>
      <Link
        to={ctaTo}
        className="inline-flex items-center gap-1.5 text-[var(--teal)] font-semibold text-sm self-start group/cta"
        data-testid={ctaTestId}
      >
        {ctaText}
        <ArrowRight
          size={16}
          className="transition-transform group-hover/cta:translate-x-1"
        />
      </Link>
    </div>
  );
}
